import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation, { canAccessDevDemos } from '../components/DeveloperNavigation';
import { billingAuthHeaders } from '../utils/billingApi';
import { getBackendBaseUrl } from '../utils/apiUrls';

const demoApiBase = `${getBackendBaseUrl()}/contract/demo`;

const sectionStyle = {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    background: '#fff',
};

const mutedBtn = {
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    padding: '8px 12px',
    background: '#f8fafc',
    color: '#94a3b8',
    cursor: 'not-allowed',
};

const primaryBtn = {
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    padding: '8px 12px',
    background: '#fff',
    cursor: 'pointer',
};

function formatCountdown(seconds) {
    if (seconds == null) return 'No demo activity yet...';
    const s = Math.max(0, Number(seconds) || 0);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
}

function DeveloperDemos() {
    const { user, isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const navigate = useNavigate();
    const email = (user?.email || '').toLowerCase();
    const allowed = canAccessDevDemos(email);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null);
    const [tick, setTick] = useState(0);
    const [skipSkyslope, setSkipSkyslope] = useState(false);
    const [mockSigningId, setMockSigningId] = useState('');
    const [mockSplitId, setMockSplitId] = useState('');

    const authHeaders = useCallback(async () => {
        return billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
    }, [getAccessTokenSilently, getIdTokenClaims]);

    const refresh = useCallback(async () => {
        if (!isAuthenticated || !allowed) return;
        try {
            setError('');
            const headers = await authHeaders();
            const res = await axios.get(`${demoApiBase}/status`, { headers });
            setStatus(res.data);
            setSkipSkyslope(Boolean(res.data?.settings?.skip_skyslope));
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, allowed, authHeaders]);

    useEffect(() => {
        if (!isAuthenticated) {
            loginWithRedirect();
            return;
        }
        if (!allowed) {
            navigate('/');
            return;
        }
        refresh();
    }, [isAuthenticated, allowed, loginWithRedirect, navigate, refresh]);

    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const clock = status?.clock || {};
    const inventory = status?.inventory || {};
    const counts = inventory.counts || {};
    let secondsRemaining = clock.seconds_remaining;
    if (clock.cleanup_due_at && secondsRemaining != null) {
        const due = Date.parse(clock.cleanup_due_at);
        if (!Number.isNaN(due)) {
            secondsRemaining = Math.max(0, Math.floor((due - Date.now()) / 1000));
        }
    }
    void tick;

    const runCleanupNow = async () => {
        if (!window.confirm(
            'Cleanup now will delete all allow-listed demo contracts, buyer agreements, '
            + 'listings, and Firestore demo state so KeyFlo looks as if the demo never happened. '
            + 'It will also best-effort clear demo calendars and archive demo-subject nathan@ '
            + 'inbox mail. SkySlope is not deleted — you must still manually delete '
            + '4521 Willow Creek Dr (sale) and 456 Pecan Creek Dr (listing). Continue?'
        )) {
            return;
        }
        setBusy(true);
        setMessage('');
        setError('');
        try {
            const headers = await authHeaders();
            const res = await axios.post(
                `${demoApiBase}/cleanup`,
                { dry_run: false },
                { headers },
            );
            const data = res.data || {};
            const calN = (data.deleted?.calendars || []).length;
            const inboxN = data.deleted?.inbox_archived || 0;
            const extras = ` Calendars deleted: ${calN}. Inbox archived: ${inboxN}.`;
            setMessage(data.success
                ? `Cleanup completed.${extras}`
                : `Cleanup finished with issues: ${(data.failures || []).length} failure(s).${extras}`);
            await refresh();
        } catch (e) {
            const data = e.response?.data;
            if (data && (Array.isArray(data.failures) || data.success === false)) {
                const calN = (data.deleted?.calendars || []).length;
                const inboxN = data.deleted?.inbox_archived || 0;
                const extras = ` Calendars deleted: ${calN}. Inbox archived: ${inboxN}.`;
                setMessage(
                    `Cleanup finished with issues: ${(data.failures || []).length} failure(s).${extras}`
                );
                await refresh();
            } else {
                setError(data?.error || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const toggleSkipSkyslope = async () => {
        const next = !skipSkyslope;
        setBusy(true);
        setMessage('');
        setError('');
        try {
            const headers = await authHeaders();
            const res = await axios.post(
                `${demoApiBase}/settings`,
                { skip_skyslope: next },
                { headers },
            );
            setSkipSkyslope(Boolean(res.data?.settings?.skip_skyslope));
            setMessage(
                next
                    ? 'Skip SkySlope saved (preference only — product enforcement in DM-4).'
                    : 'Live SkySlope sync preference restored (enforcement in DM-4).'
            );
            await refresh();
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setBusy(false);
        }
    };

    const prepareDrafts = async (selector) => {
        setBusy(true);
        setMessage('');
        setError('');
        try {
            const headers = await authHeaders();
            const res = await axios.post(
                `${demoApiBase}/prepare-drafts`,
                { selector },
                { headers },
            );
            const data = res.data || {};
            const createdN = (data.created || []).length;
            const replacedN = (data.replaced || []).length;
            if (data.success) {
                setMessage(
                    `Successfully created ${createdN} draft(s) in nathan@`
                    + (replacedN ? ` (replaced ${replacedN}).` : '.')
                );
            } else {
                setMessage(
                    `Draft prepare finished with ${(data.failures || []).length} failure(s).`
                );
            }
        } catch (e) {
            const data = e.response?.data;
            if (data && Array.isArray(data.failures)) {
                setMessage(
                    `Draft prepare finished with ${data.failures.length} failure(s).`
                );
            } else {
                setError(data?.error || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const mockInklessComplete = async () => {
        const signingId = mockSigningId.trim();
        const splitRaw = mockSplitId.trim();
        const body = {};
        if (signingId) body.signing_id = signingId;
        if (splitRaw) {
            const n = Number(splitRaw);
            if (!Number.isInteger(n) || n <= 0) {
                setError('split_id must be a positive integer');
                return;
            }
            body.split_id = n;
        }
        if (!body.signing_id && body.split_id == null) {
            setError('Enter a demo-inkless signing_id or a split_id');
            return;
        }
        setBusy(true);
        setMessage('');
        setError('');
        try {
            const headers = await authHeaders();
            const res = await axios.post(`${demoApiBase}/mock-inkless-complete`, body, { headers });
            const data = res.data || {};
            setMessage(
                `Mock Inkless complete: ${data.signing_id || signingId || 'session'}`
                + (data.status ? ` → ${data.status}` : '')
            );
            await refresh();
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (!isAuthenticated || !allowed) {
        return <LoadingSpinner />;
    }

    if (loading && !status) {
        return <LoadingSpinner />;
    }

    return (
        <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                gap: '12px',
                flexWrap: 'wrap',
            }}>
                <div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Developer • Demos</div>
                    <h1 style={{ margin: '4px 0 0', fontSize: '1.5rem' }}>Demo Mode Console</h1>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button type="button" onClick={() => navigate('/')}>Back</button>
                    <DeveloperNavigation />
                </div>
            </div>

            {error && (
                <div style={{ ...sectionStyle, marginBottom: '16px', borderColor: '#fecaca', background: '#fef2f2' }}>
                    {error}
                </div>
            )}
            {message && (
                <div style={{ ...sectionStyle, marginBottom: '16px', borderColor: '#bbf7d0', background: '#f0fdf4' }}>
                    {message}
                </div>
            )}

            <div style={{ display: 'grid', gap: '16px' }}>
                <section style={sectionStyle}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Prepare drafts</h3>
                    <p style={{ margin: '0 0 12px', color: '#475569' }}>
                        Creates / replaces scripted Gmail drafts in nathan@ in order.
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={() => prepareDrafts('all')}
                            disabled={busy}
                            style={{
                                ...primaryBtn,
                                background: '#0f172a',
                                color: '#fff',
                                border: 'none',
                                cursor: busy ? 'wait' : 'pointer',
                            }}
                        >
                            {busy ? 'Preparing…' : 'Prepare all drafts'}
                        </button>
                        <button type="button" onClick={() => prepareDrafts('pa')} disabled={busy} style={primaryBtn}>
                            PA package
                        </button>
                        <button type="button" onClick={() => prepareDrafts('ba')} disabled={busy} style={primaryBtn}>
                            Buyer agreement
                        </button>
                        <button type="button" onClick={() => prepareDrafts('la')} disabled={busy} style={primaryBtn}>
                            Listing
                        </button>
                        <button type="button" onClick={() => prepareDrafts('form')} disabled={busy} style={primaryBtn}>
                            Form generation
                        </button>
                        <button type="button" onClick={() => prepareDrafts('qa')} disabled={busy} style={primaryBtn}>
                            Q&amp;A
                        </button>
                    </div>
                </section>

                <section style={sectionStyle}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Force-complete Inkless</h3>
                    <p style={{ margin: '0 0 12px', color: '#475569' }}>
                        LA/BBSA: nathan@ &quot;Send for Signatures&quot; creates a <strong>live Inkless</strong> envelope
                        (agent slot 4) and emails the real signing link. After Nathan signs, omitted
                        principals are mocked. PA: no live signers — KeyFlo mock-completes after ~3.5s.
                        Use this backup with a live Inkless id, a <code>demo-inkless-*</code> PA id, or
                        a split id if the webhook or delayed PA complete needs a nudge.
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            value={mockSigningId}
                            onChange={(e) => setMockSigningId(e.target.value)}
                            placeholder="Inkless signing id"
                            disabled={busy}
                            style={{
                                flex: '1 1 220px',
                                minWidth: '180px',
                                padding: '8px 10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '4px',
                            }}
                        />
                        <input
                            type="text"
                            value={mockSplitId}
                            onChange={(e) => setMockSplitId(e.target.value)}
                            placeholder="split_id (optional)"
                            disabled={busy}
                            style={{
                                width: '140px',
                                padding: '8px 10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '4px',
                            }}
                        />
                        <button
                            type="button"
                            onClick={mockInklessComplete}
                            disabled={busy}
                            style={{
                                ...primaryBtn,
                                background: '#0f172a',
                                color: '#fff',
                                border: 'none',
                                cursor: busy ? 'wait' : 'pointer',
                            }}
                        >
                            {busy ? 'Completing…' : 'Force complete'}
                        </button>
                    </div>
                </section>

                <section style={sectionStyle}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Session countdown</h3>
                    <p style={{ margin: '0 0 8px', color: '#475569' }}>
                        Starts when the first demo email is processed. Auto-cleanup runs at T+1h.
                    </p>
                    <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
                        {formatCountdown(secondsRemaining)}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px' }}>
                        First action: {clock.first_action_at || '—'}
                        {clock.cleanup_due_at ? ` · Due: ${clock.cleanup_due_at}` : ''}
                    </div>
                    {clock.read_ok === false && (
                        <div style={{ fontSize: '0.85rem', color: '#b91c1c', marginTop: '8px' }}>
                            Clock unavailable{clock.read_error ? `: ${clock.read_error}` : ''}
                        </div>
                    )}
                    {status?.inbound_mailbox && (
                        <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px' }}>
                            Demo drafts address this env&apos;s inbox:&nbsp;
                            <code>{status.inbound_mailbox}</code>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={refresh}
                        disabled={busy}
                        style={{ ...primaryBtn, marginTop: '12px' }}
                    >
                        Refresh status
                    </button>
                </section>

                <section style={sectionStyle}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Skip SkySlope</h3>
                    <p style={{ margin: '0 0 12px', color: '#475569' }}>
                        Default is live SkySlope sync. Toggle to opt out for a dry demo.
                        Preference persists now; product paths honor it in DM-4.
                    </p>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: busy ? 'wait' : 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={skipSkyslope}
                            disabled={busy}
                            onChange={toggleSkipSkyslope}
                        />
                        <span>
                            {skipSkyslope
                                ? 'Skipping SkySlope (preference saved)'
                                : 'Live SkySlope sync (default)'}
                        </span>
                    </label>
                </section>

                <section style={sectionStyle}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Cleanup</h3>
                    <p style={{ margin: '0 0 12px', color: '#475569' }}>
                        Removes allow-listed demo contracts, buyer agreements, listings, and
                        Firestore demo state. Best-effort: demo calendars + demo-subject inbox.
                        Does not delete SkySlope files.
                    </p>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '0.9rem' }}>
                        <span>Contracts: <strong>{counts.contracts ?? 0}</strong></span>
                        <span>Buyer agreements: <strong>{counts.buyer_agreements ?? 0}</strong></span>
                        <span>Listings: <strong>{counts.listings ?? 0}</strong></span>
                        <span>Form sessions: <strong>{counts.form_creation_sessions ?? 0}</strong></span>
                        <span>Form emails: <strong>{counts.form_generation_emails ?? 0}</strong></span>
                        <span>Sessions: <strong>{counts.demo_sessions ?? 0}</strong></span>
                    </div>
                    <button
                        type="button"
                        onClick={runCleanupNow}
                        disabled={busy}
                        style={{
                            background: '#b91c1c',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 14px',
                            cursor: busy ? 'wait' : 'pointer',
                        }}
                    >
                        {busy ? 'Cleaning…' : 'Cleanup now'}
                    </button>
                    <div
                        role="alert"
                        style={{
                            margin: '16px 0 0',
                            padding: '16px 18px',
                            border: '2px solid #b45309',
                            borderRadius: '8px',
                            background: '#fffbeb',
                            color: '#78350f',
                        }}
                    >
                        <div style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '8px' }}>
                            SkySlope is not deleted by this cleanup
                        </div>
                        <p style={{ margin: '0 0 10px', fontSize: '1rem', lineHeight: 1.45 }}>
                            After you run cleanup here, delete these SkySlope records
                            manually in the SkySlope UI:
                        </p>
                        <ul style={{ margin: 0, paddingLeft: '22px', fontSize: '1.05rem', lineHeight: 1.55 }}>
                            <li><strong>Sale:</strong> 4521 Willow Creek Dr</li>
                            <li><strong>Listing:</strong> 456 Pecan Creek Dr</li>
                        </ul>
                    </div>
                    <ul style={{ marginTop: '16px', paddingLeft: '18px', color: '#475569' }}>
                        {(inventory.manual_checklist || []).map((item) => (
                            <li key={item.id} style={{ marginBottom: '6px' }}>
                                {item.required ? <strong>Required: </strong> : null}
                                {item.label}
                            </li>
                        ))}
                    </ul>
                </section>

                <section style={sectionStyle}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Recapture fixtures</h3>
                    <p style={{ margin: '0 0 12px', color: '#475569' }}>
                        Refresh fixture snapshots from a real non-demo run. Wired in DM-5.
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button type="button" disabled style={mutedBtn} title="Coming in DM-5">
                            Recapture all
                        </button>
                        <button type="button" disabled style={mutedBtn} title="Coming in DM-5">PA steps</button>
                        <button type="button" disabled style={mutedBtn} title="Coming in DM-5">BA</button>
                        <button type="button" disabled style={mutedBtn} title="Coming in DM-5">Listing</button>
                        <button type="button" disabled style={mutedBtn} title="Coming in DM-5">Q&amp;A</button>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default DeveloperDemos;
